import type { Config } from "wagmi"
import { readContract } from "@wagmi/core"

import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { IChunkedRpcStrategy } from "@/domains/library/rpc-strategy"
import type { DefaultInfoPairValues } from "@/domains/on-chain/scalens/frontend-default-info-pairs"
import { LcAddress } from "@/domains/core"
import { frontendDefaultInfoPairs } from "@/domains/on-chain/scalens/frontend-default-info-pairs"
import { scalensUserInfoPairs, ScalensUserInfoPairs } from "@/domains/on-chain/scalens/frontend-user-info-pairs"
import {
  scalensUserManageMinimalPoolInfo,
  ScalensUserManageMinimalPoolInfo,
} from "@/domains/on-chain/scalens/frontend-user-manage-minimal-pool"

import { MinimalPoolStore } from "./minimal-pool-store"

export type MinimalPoolOnChainTransportAddresses = {
  scalensDefaultInfoPairs?: LcAddress
  scalensUserInfoPairs?: LcAddress
  scalensUserManageMinimalPoolInfo?: LcAddress
}

export class MinimalPoolOnChainTransport {
  constructor(
    private config: Config,
    private readonly addresses: MinimalPoolOnChainTransportAddresses,
    private chunkedRpcStrategy: IChunkedRpcStrategy,
  ) {}

  /**
   * Load classic pool info pairs from on-chain
   * Pure data loading - no state, no caching, no side effects
   */
  async loadMinimalPoolInfoPairs(
    pools: Array<MinimalPoolStore>,
    query: IOrchestratedQuerySender<any>,
    defaultChunkSize: number = 70,
  ): Promise<Array<DefaultInfoPairValues>> {
    if (!this.addresses.scalensDefaultInfoPairs) {
      return []
    }

    // Use strategy to execute chunked RPC calls
    // Strategy handles chunking, metrics, and error handling
    const chunkResults = await this.chunkedRpcStrategy.executeChunked(
      pools,
      query,
      defaultChunkSize,
      async (chunk: MinimalPoolStore[], chunkIndex: number) => {
        try {
          return await frontendDefaultInfoPairs(
            this.config,
            this.addresses.scalensDefaultInfoPairs!,
            chunk,
          )
        } catch (error) {
          console.warn(
            `[MinimalPoolOnChainTransport] Skipping infoPairs chunk ${chunkIndex}; using cached pool values.`,
            error instanceof Error ? error.message : String(error),
          )
          return []
        }
      },
      query.getRpcMetricsCollector(),
    )

    return chunkResults.flat()
  }

  async loadMinimalPoolUserInfoPairs(
    who: LcAddress,
    pools: Array<MinimalPoolStore>,
    query: IOrchestratedQuerySender<any>,
    defaultChunkSize: number = 100,
  ): Promise<ScalensUserInfoPairs[]> {
    if (!this.addresses.scalensUserInfoPairs) {
      return []
    }

    // Use strategy to execute chunked RPC calls
    // Strategy handles chunking, metrics, and error handling
    const chunkResults = await this.chunkedRpcStrategy.executeChunked(
      pools,
      query,
      defaultChunkSize,
      async (chunk: MinimalPoolStore[], chunkIndex: number) => {
        try {
          // Call the on-chain domain function for this chunk
          return await scalensUserInfoPairs({
            who,
            scalensUserInfoPairs: this.addresses.scalensUserInfoPairs!,
            pools: chunk, // Pass single chunk
            config: this.config,
          })
        } catch {
          if (import.meta.env.DEV) {
            console.debug(`[MinimalPoolOnChainTransport] Skipping userInfoPairs chunk ${chunkIndex}; lens call failed.`)
          }
          return []
        }
      },
      query.getRpcMetricsCollector(),
    )

    // Flatten results from all chunks
    return chunkResults.flat()
  }

  async loadUserManageMinimalPoolInfo(
    who: LcAddress,
    pool: MinimalPoolStore,
    routerAddress: LcAddress,
  ): Promise<ScalensUserManageMinimalPoolInfo | undefined> {
    if (!this.addresses.scalensUserManageMinimalPoolInfo) {
      return undefined
    }

    const userManageMinimalPoolInfo = await scalensUserManageMinimalPoolInfo({
      who,
      scalensUserManageMinimalPool: this.addresses.scalensUserManageMinimalPoolInfo,
      pool,
      routerAddress,
      config: this.config,
    })

    return userManageMinimalPoolInfo ?? undefined
  }
}
